import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import {
  TextField,
  DatePicker,
  PrimaryButton,
  DefaultButton,
  Stack,
  Label,
  MessageBar,
  MessageBarType,
} from "office-ui-fabric-react";

const SimpleDynamicForm = ({
  formFields = [],
  defaultValues = {},
  onSubmit,
  onDiscard,
  successMessage,
  errorMessage,
}) => {
  const [formData, setFormData] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isSaved && (formData.name || formData.document)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isSaved, formData]);

  function onSuccessCallback() {
    if (onDiscard instanceof Function) {
      setIsSaved(true);
      toast.success(successMessage, {
        position: "top-right",
        delay: 2000,
        onClose: () => {
          onDiscard();
        },
      });
    }
  }

  function onFailureCallback() {
    toast.error(errorMessage, {
      position: "top-right",
      delay: 2000,
      onClose: () => {
        onDiscard();
      },
    });
  }

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFileChange = (fieldName, event) => {
    const file = event.target.files[0];
    setFormData((prev) => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    formFields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label || field.name} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      onSuccessCallback();
    } catch (error) {
      onFailureCallback(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const { name, type, label, required, placeholder, maxDate, ...fieldProps } =
      field;
    const fieldValue = formData[name] || "";
    const hasError = !!errors[name];

    switch (type) {
      case "text":
      case "email":
      case "textarea":
        return (
          <TextField
            key={name}
            label={label || name}
            value={fieldValue}
            onChange={(e, value) => handleInputChange(name, value)}
            required={required}
            placeholder={placeholder}
            multiline={type === "textarea"}
            rows={type === "textarea" ? 4 : undefined}
            errorMessage={errors[name]}
            {...fieldProps}
          />
        );

      case "date":
        return (
          <div key={name}>
            <Label required={required}>{label || name}</Label>
            <DatePicker
              value={fieldValue ? new Date(fieldValue) : null}
              onSelectDate={(date) => handleInputChange(name, date)}
              placeholder={placeholder || "Select a date"}
              maxDate={maxDate}
              {...fieldProps}
            />
            {hasError && <div className="error-text">{errors[name]}</div>}
          </div>
        );

      case "file":
        return (
          <div key={name}>
            <Label required={required}>{label || name}</Label>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const allowedTypes = field.accept
                    .split(",")
                    .map((ext) => ext.trim().toLowerCase());

                  const fileExt = `.${file.name
                    .split(".")
                    .pop()
                    .toLowerCase()}`;

                  if (!allowedTypes.includes(fileExt)) {
                    setErrors((prev) => ({
                      ...prev,
                      [name]: `Invalid file type. Allowed: ${field.accept}`,
                    }));
                    setFormData((prev) => ({ ...prev, [name]: null }));
                    e.target.value = "";
                    return;
                  }
                  setErrors((prev) => ({
                    ...prev,
                    [name]: null,
                  }));
                  handleFileChange(name, e);
                }
              }}
              accept={field.accept || "*/*"}
              className={`file-input ${hasError ? "error" : ""}`}
              {...fieldProps}
            />
            <div className="hint-text">Allowed file types: {field.accept}</div>

            {hasError && <div className="error-text">{errors[name]}</div>}

            {formData[name] && (
              <div className="hint-text">Selected: {formData[name].name}</div>
            )}
          </div>
        );

      case "number":
        return (
          <TextField
            key={name}
            label={label || name}
            type="number"
            value={fieldValue.toString()}
            onChange={(e, value) =>
              handleInputChange(name, parseFloat(value) || 0)
            }
            required={required}
            placeholder={placeholder}
            errorMessage={errors[name]}
            {...fieldProps}
          />
        );

      default:
        return (
          <TextField
            key={name}
            label={label || name}
            value={fieldValue}
            onChange={(e, value) => handleInputChange(name, value)}
            required={required}
            placeholder={placeholder}
            errorMessage={errors[name]}
            {...fieldProps}
          />
        );
    }
  };

  return (
    <>
      <ToastContainer />
      <form onSubmit={handleSubmit}>
        <Stack tokens={{ childrenGap: 15 }}>
          {Object.values(errors).some((err) => err) && (
            <MessageBar messageBarType={MessageBarType.error}>
              Please fix the errors below before submitting.
            </MessageBar>
          )}

          {formFields.map((field) => renderField(field))}

          <Stack
            horizontal
            tokens={{ childrenGap: 10 }}
            style={{ marginTop: 20 }}
          >
            <PrimaryButton type="submit" text="Save" disabled={isSubmitting} />
            <DefaultButton
              text="Cancel"
              onClick={onDiscard}
              disabled={isSubmitting}
            />
          </Stack>
        </Stack>
      </form>
    </>
  );
};

export default SimpleDynamicForm;
